import React, { useState } from "react";
import {
  Factory,
  Warehouse,
  Bot,
  Building2,
  Sprout,
  Tablet,
  Scan,
  ChevronRight,
  CheckCheck,
} from "lucide-react";

export const ExploreSolutionsSection = () => {
  const [activeTab, setActiveTab] = useState("industrial");

  const solutions = [
    {
      id: "industrial",
      title: "Industrial & Manufacturing",
      icon: Factory,
      iconColor: "#0066FF",
      iconBg: "rgba(0, 102, 255, 0.1)",
      category: "INDUSTRIAL & MANUFACTURING",
      headlinePrefix: "Smarter factories",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Deploy intelligent robots to automate material movement, inspection, assembly support and facility operations.",
      features: [
        "Increase productivity",
        "Reduce operational costs",
        "Safe and consistent operations",
        "Easy integration with existing systems",
      ],
      relatedProducts: [
        {
          name: "AMR & AGV",
          desc: "Autonomous material movement and logistics",
          img: "/images/physical-ai/product-amr-agv.png",
        },
        {
          name: "Collaborative Robots",
          desc: "Flexible human-machine collaboration",
          img: "/images/physical-ai/product-cobot.png",
        },
        {
          name: "Robotic Grippers",
          desc: "Intelligent manipulation & handling",
          img: "/images/physical-ai/product-gripper.png",
        },
      ],
      image: "/images/physical-ai/industrial-manufacturing-arm.jpg",
      stats: [
        { label: "Higher Productivity", value: "+40%" },
        { label: "Fewer Errors", value: "-60%" },
        { label: "Faster Deployment", value: "3x" },
      ],
    },
    {
      id: "warehousing",
      title: "Warehousing & Logistics",
      icon: Warehouse,
      iconColor: "#9333EA",
      iconBg: "rgba(147, 51, 234, 0.1)",
      category: "WAREHOUSING & LOGISTICS",
      headlinePrefix: "Intelligent logistics",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Autonomous robots and orchestration systems for modern warehouses and distribution centers.",
      features: [
        "Faster order fulfilment",
        "Optimized fleet operations",
        "Higher storage and throughput",
        "Seamless WMS/WCS integration",
      ],
      relatedProducts: [
        {
          name: "AMR & AGV",
          desc: "Autonomous material movement and logistics",
          img: "/images/physical-ai/product-amr-agv.png",
        },
        {
          name: "Collaborative Robots",
          desc: "Flexible human-machine collaboration",
          img: "/images/physical-ai/product-cobot.png",
        },
        {
          name: "Robotic Grippers",
          desc: "Intelligent manipulation & handling",
          img: "/images/physical-ai/product-gripper.png",
        },
      ],
      image: "/images/physical-ai/warehousing-logistics-amr.jpg",
      stats: [
        { label: "Throughput Increase", value: "+70%" },
        { label: "Operational Costs", value: "-50%" },
        { label: "Order Accuracy", value: "99.9%" },
      ],
    },
    {
      id: "education",
      title: "Education & Research",
      icon: Tablet,
      iconColor: "#0284C7",
      iconBg: "rgba(2, 132, 199, 0.1)",
      category: "EDUCATION & RESEARCH",
      headlinePrefix: "Hands-on learning",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Educational robots and research platforms to empower students, researchers and innovators in building the next generation of AI-powered robotic systems.",
      features: [
        "Hands-on robotics education and skill development",
        "Modular platforms for research and innovation",
        "Support for ROS2, simulation and AI development",
        "Scalable from classrooms to advanced research labs",
      ],
      relatedProducts: [
        {
          name: "Educational Robots",
          desc: "Robotics platforms for learning and research",
          img: "/images/physical-ai/product-edu-robot.png",
        },
        {
          name: "Collaborative Robots",
          desc: "Flexible human-machine collaboration",
          img: "/images/physical-ai/product-cobot.png",
        },
        {
          name: "Robotic Grippers",
          desc: "Intelligent manipulation & handling",
          img: "/images/physical-ai/product-gripper.png",
        },
      ],
      image: "/images/physical-ai/education-research.jpg",
      stats: [
        { label: "Learning Engagement", value: "3x" },
        { label: "Skill Development", value: "+70%" },
        {
          label: "Research Adoption",
          value: "500+",
          sub: "Universities & Labs",
        },
      ],
    },
    {
      id: "security",
      title: "Security & Surveillance",
      icon: Scan,
      iconColor: "#06B6D4",
      iconBg: "rgba(6, 182, 212, 0.1)",
      category: "SECURITY & SURVEILLANCE",
      headlinePrefix: "Safer environments",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Autonomous robots for inspection, monitoring and surveillance across critical infrastructure, commercial facilities and public spaces.",
      features: [
        "Autonomous patrolling and real-time monitoring",
        "AI-powered anomaly detection",
        "Operation in complex and hazardous environments",
        "24/7 surveillance with consistent performance",
      ],
      relatedProducts: [
        {
          name: "Quadruped Robots",
          desc: "Mobile inspection and monitoring",
          img: "/images/physical-ai/product-quadruped.png",
        },
        {
          name: "Security Humanoids",
          desc: "All-terrain security and inspection",
          img: "/images/physical-ai/product-service-humanoid.png",
        },
        {
          name: "Security Humanoids",
          desc: "Autonomous security patrol and facility monitoring",
          img: "/images/physical-ai/product-service-humanoid.png",
        },
      ],
      image: "/images/physical-ai/security-surveillance.png",
      stats: [
        { label: "Detection Speed", value: "5x" },
        { label: "Operational Costs", value: "-60%" },
        { label: "Surveillance Uptime", value: "99.9%" },
      ],
    },
    {
      id: "hospitality",
      title: "Hospitality & Service",
      icon: Building2,
      iconColor: "#EF4444",
      iconBg: "rgba(239, 68, 68, 0.1)",
      category: "HOSPITALITY & SERVICE",
      headlinePrefix: "Better experiences",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Service robots and intelligent automation for hotels, restaurants, healthcare and public spaces.",
      features: [
        "Enhanced customer experience",
        "Reliable and consistent service",
        "Reduce operational workload",
        "Safe collaboration with people",
      ],
      relatedProducts: [
        {
          name: "Service Humanoids",
          desc: "Intelligent assistance for service environments",
          img: "/images/physical-ai/product-service-humanoid.png",
        },
        {
          name: "Industrial Delivery Robots",
          desc: "Autonomous delivery across facilities",
          img: "/images/physical-ai/product-delivery-robot.png",
        },
        {
          name: "Robotic Grippers",
          desc: "Intelligent manipulation and handling",
          img: "/images/physical-ai/product-gripper.png",
        },
      ],
      image: "/images/physical-ai/hospitality-service.jpg",
      stats: [
        { label: "Satisfaction", value: "+50%" },
        { label: "Operational Costs", value: "-40%" },
        { label: "Service Efficiency", value: "3x" },
      ],
    },
    {
      id: "cleaning",
      title: "Commercial Cleaning",
      icon: Bot,
      iconColor: "#F59E0B",
      iconBg: "rgba(245, 158, 11, 0.1)",
      category: "COMMERCIAL CLEANING",
      headlinePrefix: "Cleaner spaces",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Autonomous cleaning robots to maintain clean, healthy and safe environments across offices, commercial buildings, airports, malls and public facilities.",
      features: [
        "Autonomous floor and facility cleaning",
        "Consistent and high-quality cleaning results",
        "Reduce labor and operational costs",
        "Real-time monitoring and reporting",
      ],
      relatedProducts: [
        {
          name: "Commercial Cleaning Robots",
          desc: "Autonomous floor and facility cleaning",
          img: "/images/physical-ai/product-cleaning-robot.png",
        },
        {
          name: "Industrial Floor Cleaners",
          desc: "For large industrial facilities",
          img: "/images/physical-ai/product-floor-cleaner.png",
        },
      ],
      image: "/images/physical-ai/commercial-cleaning.jpg",
      stats: [
        { label: "Cleaning Efficiency", value: "+60%" },
        { label: "Operational Costs", value: "-50%" },
        { label: "Uptime", value: "99.9%" },
      ],
    },
    {
      id: "agriculture",
      title: "Agriculture & Outdoor",
      icon: Sprout,
      iconColor: "#10B981",
      iconBg: "rgba(16, 185, 129, 0.1)",
      category: "AGRICULTURE & OUTDOOR",
      headlinePrefix: "Outdoor operations",
      headlineSuffix: "with Physical",
      headlineAccent: "AI.",
      description:
        "Autonomous robots to maintain green spaces, farms and outdoor environments with precision, efficiency and sustainability.",
      features: [
        "Autonomous mowing and outdoor maintenance",
        "Consistent and high-quality results",
        "Reduces manual labor and operational costs",
        "Adapts to complex outdoor environments",
      ],
      relatedProducts: [
        {
          name: "Autonomous Lawn Mowers",
          desc: "Intelligent outdoor maintenance",
          img: "/images/physical-ai/product-lawn-mower.png",
        },
        {
          name: "Quadruped Robots",
          desc: "Mobile inspection and monitoring",
          img: "/images/physical-ai/product-quadruped.png",
        },
        {
          name: "Vision & Sensing Robots",
          desc: "Inspection, perception and machine intelligence",
          img: "/images/physical-ai/product-vision-robot.png",
        },
      ],
      image: "/images/physical-ai/agriculture-outdoor.jpg",
      stats: [
        { label: "Efficiency", value: "+70%" },
        { label: "Operational Costs", value: "-50%" },
        { label: "Coverage per Day", value: "3x" },
      ],
    },
  ];

  const current = solutions.find((s) => s.id === activeTab) || solutions[0];

  return (
    <section
      id="explore-physical-ai-solutions"
      className="relative w-full py-16 sm:py-20 lg:py-24"
      style={{ backgroundColor: "rgba(242, 248, 252, 1)" }}
    >
      <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
        {/* Section Eyebrow */}
        <p className="text-[12px] font-bold tracking-[2.5px] uppercase text-[#94A3B8] mb-8">
          EXPLORE PHYSICAL AI SOLUTIONS
        </p>

        {/* Main Card Container */}
        <div className="w-full bg-white rounded-[28px] sm:rounded-[32px] border border-[#E2EDF8] shadow-[0_10px_40px_rgba(0,0,0,0.03)] p-4 sm:p-6 lg:p-7 xl:p-8 flex flex-col lg:flex-row items-stretch gap-6 lg:gap-8">
          {/* Left Column: Navigation Tabs List */}
          <div className="w-full lg:w-[260px] xl:w-[285px] shrink-0 flex flex-col gap-2 border-b lg:border-b-0 lg:border-r border-[#EEF2F6] pb-6 lg:pb-0 lg:pr-5">
            {solutions.map((item) => {
              const isActive = activeTab === item.id;
              const IconComp = item.icon;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between px-4 py-3.5 rounded-[18px] transition-all duration-300 text-left cursor-pointer group ${
                    isActive
                      ? "shadow-sm"
                      : "hover:bg-[#F8FBFE]"
                  }`}
                  style={{
                    backgroundColor: isActive
                      ? item.id === "industrial"
                        ? "rgba(235, 244, 255, 1)"
                        : item.id === "warehousing"
                        ? "rgba(245, 230, 255, 1)"
                        : item.id === "education"
                        ? "rgba(230, 243, 255, 1)"
                        : item.id === "security"
                        ? "rgba(224, 246, 255, 1)"
                        : item.id === "hospitality"
                        ? "rgba(254, 232, 232, 1)"
                        : item.id === "cleaning"
                        ? "rgba(254, 243, 226, 1)"
                        : "rgba(230, 250, 235, 1)"
                      : "transparent",
                    border: isActive
                      ? `1.5px solid ${
                          item.id === "industrial"
                            ? "#BFDBFE"
                            : item.id === "warehousing"
                            ? "#E9D5FF"
                            : item.id === "education"
                            ? "#BAE6FD"
                            : item.id === "security"
                            ? "#A5F3FC"
                            : item.id === "hospitality"
                            ? "#FECACA"
                            : item.id === "cleaning"
                            ? "#FED7AA"
                            : "#BBF7D0"
                        }`
                      : "1.5px solid transparent",
                  }}
                >
                  <div className="flex items-center gap-3.5">
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] transition-transform duration-200 group-hover:scale-105"
                      style={{
                        backgroundColor: isActive ? "rgba(255, 255, 255, 0.9)" : item.iconBg,
                      }}
                    >
                      <IconComp
                        className="h-5 w-5"
                        style={{ color: item.iconColor }}
                        strokeWidth={2.2}
                      />
                    </span>
                    <span
                      className={`text-[14px] sm:text-[14.5px] font-bold leading-tight ${
                        isActive ? "text-[#0F172A]" : "text-[#334155]"
                      }`}
                    >
                      {item.title}
                    </span>
                  </div>

                  <ChevronRight
                    className={`h-4 w-4 transition-transform duration-200 ${
                      isActive
                        ? "translate-x-0.5 text-[#0F172A]"
                        : "text-[#94A3B8] opacity-60 group-hover:translate-x-0.5 group-hover:opacity-100"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          {/* Right Content Area: Active Tab Details */}
          <div className="flex-1 min-w-0 flex flex-col xl:flex-row items-start justify-between gap-6 xl:gap-8 p-2 sm:p-4 lg:p-5 xl:p-6">
            {/* Middle Column: Text, Checklist & Related Products */}
            <div className="flex-1 min-w-0 flex flex-col items-start max-w-[580px]">
              {/* Category Eyebrow */}
              <p className="text-[11px] font-bold uppercase tracking-[2.4px] text-[#8C9AA8]">
                {current.category}
              </p>

              {/* Main Headline */}
              <h2
                className="mt-3 text-[24px] sm:text-[28px] lg:text-[30px] xl:text-[34px] font-extrabold leading-[1.2] tracking-[-0.6px] text-[#0F172A]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span className="block sm:whitespace-nowrap">{current.headlinePrefix}</span>
                <span className="block sm:whitespace-nowrap">
                  <span>{current.headlineSuffix} </span>
                  <span className="text-[#0066FF]">{current.headlineAccent}</span>
                </span>
              </h2>

              {/* Description Paragraph */}
              <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[22px] text-[#64748B]">
                {current.description}
              </p>

              {/* Features Checklist */}
              <div className="mt-6 flex flex-col gap-2.5">
                {current.features.map((feat) => (
                  <div key={feat} className="flex items-center gap-2.5">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[#0066FF]">
                      <CheckCheck className="h-4 w-4" strokeWidth={2.6} />
                    </span>
                    <span className="text-[13.5px] sm:text-[14px] font-medium text-[#1E293B]">
                      {feat}
                    </span>
                  </div>
                ))}
              </div>

              {/* Related Products Section */}
              <div className="mt-8 w-full">
                <p className="text-[10.5px] sm:text-[11px] font-bold uppercase tracking-[2.2px] text-[#8C9AA8] mb-3">
                  RELATED PRODUCTS
                </p>
                <div
                  className={`grid ${
                    current.relatedProducts.length === 2
                      ? "grid-cols-2 max-w-[360px]"
                      : "grid-cols-3"
                  } gap-2.5 sm:gap-3.5 w-full`}
                >
                  {current.relatedProducts.map((prod) => (
                    <div
                      key={prod.name}
                      className="flex flex-col items-start rounded-[16px] bg-[#F8FBFE] border border-[#E4EFFB] p-3 sm:p-3.5 hover:border-[#93C5FD] hover:shadow-md transition-all duration-200 group"
                    >
                      <div className="w-full h-16 sm:h-20 flex items-center justify-center overflow-hidden mb-2">
                        <img
                          src={prod.img}
                          alt={prod.name}
                          className="h-full max-w-full object-contain filter drop-shadow-sm transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <h4 className="text-[12px] sm:text-[12.5px] font-bold text-[#0F172A] leading-tight">
                        {prod.name}
                      </h4>
                      <p className="mt-1 text-[10px] sm:text-[10.5px] leading-[14px] text-[#64748B] line-clamp-2">
                        {prod.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Hero Robot Arm Image + Floating Stat Card */}
            <div className="relative w-full xl:w-[460px] 2xl:w-[500px] shrink-0 mt-6 xl:mt-0">
              {/* Main Image with rounded corners matching screenshot */}
              <div className="relative w-full h-[400px] sm:h-[460px] md:h-[520px] lg:h-[560px] rounded-[20px] sm:rounded-[24px] overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.08)] bg-slate-100">
                <img
                  src={current.image}
                  alt={current.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating Stat Card with exact Figma specs: width 150px, height 232.08px, radius 14.15px, gap 18.4px */}
              <div
                className="absolute bottom-5 right-4 sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-6 z-20 flex flex-col justify-between animate-cardFloat"
                style={{
                  width: "150px",
                  minHeight: "232.08px",
                  backgroundColor: "rgba(244, 250, 254, 1)",
                  borderRadius: "14.15px",
                  border: "1.42px solid rgba(223, 239, 249, 1)",
                  padding: "16.98px",
                  gap: "18.4px",
                  boxShadow: "0px 9.91px 19.81px rgba(46, 88, 120, 0.16)",
                }}
              >
                {current.stats.map((st) => (
                  <div key={st.label} className="flex flex-col items-start">
                    <span className="text-[11px] font-bold text-[#0F172A] leading-tight tracking-[-0.2px] whitespace-nowrap">
                      {st.label}
                    </span>
                    <span
                      className="text-[26px] sm:text-[28px] font-extrabold text-[#2F73F6] leading-none mt-1 tracking-tight"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {st.value}
                    </span>
                    {st.sub && (
                      <span className="text-[9.5px] font-semibold text-[#64748B] mt-0.5">
                        {st.sub}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreSolutionsSection;
